import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Upload, Check, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer } from '../utils/motion';
import { MotionButton } from '../components/ui/motion-button';
import { serviceRequestAPI } from '../services/api';

export const RequestService = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [referenceId, setReferenceId] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Contact & Company
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    company: '',
    country: '',
    city: '',
    
    // Step 2: Service Need
    services: [],
    budget: '',
    timeline: '',
    description: '',
    files: [],
    consent: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceChange = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for API
      const requestData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        whatsapp: formData.whatsapp || null,
        company: formData.company,
        country: formData.country || null,
        city: formData.city || null,
        services: formData.services,
        budget: formData.budget || null,
        timeline: formData.timeline || null,
        description: formData.description,
        consent: formData.consent
      };

      const response = await serviceRequestAPI.submitServiceRequest(requestData, formData.files);
      
      if (response.success) {
        setSubmitStatus('success');
        setReferenceId(response.reference_id);
        // Reset form
        setFormData({
          name: '', email: '', phone: '', whatsapp: '', company: '', country: '', city: '',
          services: [], budget: '', timeline: '', description: '', files: [], consent: false
        });
        setCurrentStep(1);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Service request submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.name && formData.email && formData.company;
  const isStep2Valid = formData.services.length > 0 && formData.description && formData.consent;

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <motion.div
              variants={revealVariants}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <span>Request Service</span>
            </motion.div>

            <motion.h1 
              variants={revealVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Start Your <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Project</span>
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Tell us about your project requirements and let our experts create a tailored solution for your needs.
            </motion.p>
          </motion.div>

          {/* Progress Indicator */}
          <motion.div
            variants={revealVariants}
            initial="hidden"
            animate="visible"
            className="max-w-md mx-auto mb-12"
          >
            <div className="flex items-center justify-between">
              {[1, 2].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                    currentStep >= step 
                      ? 'bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {currentStep > step ? <Check size={16} /> : step}
                  </div>
                  {step < 2 && (
                    <div className={`flex-1 h-0.5 mx-4 transition-colors ${
                      currentStep > step ? 'bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A]' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-3 text-sm">
              <span className={currentStep >= 1 ? 'text-[#1F3C88] font-medium' : 'text-gray-600'}>
                Contact Info
              </span>
              <span className={currentStep >= 2 ? 'text-[#1F3C88] font-medium' : 'text-gray-600'}>
                Service Details
              </span>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            variants={revealVariants}
            initial="hidden"
            animate="visible"
            className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl p-8"
          >
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="space-y-6"
                  >
                    <motion.h2 variants={revealVariants} className="text-2xl font-bold text-gray-900 mb-8">
                      Contact & Company Information
                    </motion.h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="Your full name"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="your.email@company.com"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="+971 XX XXX XXXX"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          WhatsApp Number
                        </label>
                        <input
                          type="tel"
                          name="whatsapp"
                          value={formData.whatsapp}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="+971 XX XXX XXXX"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="Your company name"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="United Arab Emirates"
                        />
                      </motion.div>

                      <motion.div variants={revealVariants} className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                          placeholder="Dubai"
                        />
                      </motion.div>
                    </div>

                    <motion.div variants={revealVariants} className="flex justify-end">
                      <MotionButton
                        type="button"
                        onClick={handleNext}
                        disabled={!isStep1Valid}
                        variant="primary"
                        size="lg"
                      >
                        <div className="flex items-center space-x-2">
                          <span>Next Step</span>
                          <ArrowRight size={20} />
                        </div>
                      </MotionButton>
                    </motion.div>
                  </motion.div>
                )}

                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="space-y-6"
                  >
                    <motion.h2 variants={revealVariants} className="text-2xl font-bold text-gray-900 mb-8">
                      Service Requirements
                    </motion.h2>

                    <motion.div variants={revealVariants}>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Select Services *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {mockData.services.map((service) => (
                          <div
                            key={service.id}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.services.includes(service.id)
                                ? 'border-[#1F3C88] bg-[#1F3C88]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleServiceChange(service.id)}
                          >
                            <div className="font-medium text-gray-900 mb-1">{service.name}</div>
                            <div className="text-sm text-gray-600">{service.description}</div>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Budget Range
                        </label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                        >
                          <option value="">Select budget range</option>
                          <option value="under-50k">Under $50,000</option>
                          <option value="50k-100k">$50,000 - $100,000</option>
                          <option value="100k-250k">$100,000 - $250,000</option>
                          <option value="250k-500k">$250,000 - $500,000</option>
                          <option value="over-500k">Over $500,000</option>
                        </select>
                      </motion.div>

                      <motion.div variants={revealVariants}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timeline
                        </label>
                        <select
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                        >
                          <option value="">Select timeline</option>
                          <option value="immediate">Immediate (within 1 month)</option>
                          <option value="short">Short-term (1-3 months)</option>
                          <option value="medium">Medium-term (3-6 months)</option>
                          <option value="long">Long-term (6+ months)</option>
                        </select>
                      </motion.div>
                    </div>

                    <motion.div variants={revealVariants}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Description *
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors resize-none"
                        placeholder="Please describe your project requirements, objectives, and any specific challenges you're facing..."
                      />
                    </motion.div>

                    <motion.div variants={revealVariants}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Attach Files (Optional)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
                        <Upload size={48} className="text-gray-400 mx-auto mb-4" />
                        <div className="text-gray-600 mb-2">
                          Drag & drop files here, or{' '}
                          <label className="text-[#1F3C88] hover:text-[#FF5C8A] cursor-pointer font-medium">
                            browse
                            <input
                              type="file"
                              multiple
                              onChange={handleFileChange}
                              className="hidden"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                          </label>
                        </div>
                        <div className="text-xs text-gray-500">
                          Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
                        </div>
                      </div>
                      {formData.files.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {formData.files.map((file, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                              <Check size={16} className="text-green-500" />
                              <span>{file.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>

                    <motion.div variants={revealVariants}>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          required
                          className="mt-1 h-5 w-5 text-[#1F3C88] rounded focus:ring-[#1F3C88] border-gray-300"
                        />
                        <span className="text-sm text-gray-600 leading-relaxed">
                          I consent to SAX Project Management Services L.L.C contacting me about this request 
                          and agree to the processing of my personal data in accordance with the privacy policy.
                        </span>
                      </label>
                    </motion.div>

                    <motion.div variants={revealVariants} className="flex justify-between">
                      <MotionButton
                        type="button"
                        onClick={handlePrevious}
                        variant="outline"
                        size="lg"
                      >
                        <div className="flex items-center space-x-2">
                          <ArrowLeft size={20} />
                          <span>Previous</span>
                        </div>
                      </MotionButton>

                      <MotionButton
                        type="submit"
                        disabled={!isStep2Valid || isSubmitting}
                        variant="secondary"
                        size="lg"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{isSubmitting ? 'Submitting...' : 'Submit Request'}</span>
                          {!isSubmitting && <ArrowRight size={20} />}
                        </div>
                      </MotionButton>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            {/* Success/Error Messages */}
            <AnimatePresence>
              {submitStatus && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-8"
                >
                  {submitStatus === 'success' ? (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} className="text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-800 mb-4">Request Submitted Successfully!</h3>
                      <div className="bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-6 py-3 rounded-xl inline-block mb-4">
                        <p className="font-bold text-lg">Reference ID: {referenceId}</p>
                      </div>
                      <p className="text-green-700 mb-6">
                        Thank you for your service request. Our expert team will review your requirements and contact you within 24 hours.
                      </p>
                      <div className="space-y-3">
                        <p className="text-sm text-green-600">
                          <strong>Next steps:</strong> Save your reference ID and check your email for confirmation details.
                        </p>
                        <div className="flex justify-center space-x-4">
                          <button
                            onClick={() => {
                              const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=Hi, I just submitted a service request with reference ID: ${referenceId}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            className="inline-flex items-center space-x-2 bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#20b358] transition-colors"
                          >
                            <span>WhatsApp Us</span>
                            <ExternalLink size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle size={32} className="text-red-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-red-800 mb-4">Submission Failed</h3>
                      <p className="text-red-700 mb-6">
                        Sorry, there was an error submitting your service request. Please try again or contact us directly.
                      </p>
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={() => setSubmitStatus(null)}
                          className="bg-[#1F3C88] text-white px-6 py-2 rounded-lg hover:bg-[#1a3373] transition-colors"
                        >
                          Try Again
                        </button>
                        <button
                          onClick={() => {
                            const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=Hi, I need help with submitting a service request.`;
                            window.open(whatsappUrl, '_blank');
                          }}
                          className="bg-[#25D366] text-white px-6 py-2 rounded-lg hover:bg-[#20b358] transition-colors"
                        >
                          Contact Support
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
};